import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CommonButton from '../../../components/common/CommonButton/CommonButton';


/**
 * Portfolio Actions Modal Component
 * Displays options to edit or delete a portfolio item
 * 
 * @param {Object} props
 * @param {boolean} props.open - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.rowData - Selected row data
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onDelete - Delete handler
 */
const PortfolioActionsModal = ({ 
  open, 
  onClose, 
  rowData, 
  onEdit, 
  onDelete 
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(rowData);
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleEditClick = () => {
    onEdit(rowData);
    onClose();
  };

  const handleClose = () => {
    setShowDeleteConfirm(false);
    onClose();
  };

  if (!rowData) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: showDeleteConfirm ? '280px' : '200px',
        }
      }}
    >
      {!showDeleteConfirm ? (
        <>
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1,
          }}>
            <Typography variant="h6" component="div">
              {rowData.company}
            </Typography>
            <IconButton 
              onClick={handleClose}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            <List sx={{ pt: 0}}>
              <ListItem disablePadding >
                <ListItemButton 
                  onClick={handleEditClick}
                  sx={{
                    display: "flex",
                    gap: 3,
                    py: 2,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  <ListItemIcon>
                    <EditIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Edit Portfolio Item"
                    secondary="Modify company details and metrics"
                  />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton 
                  onClick={handleDeleteClick}
                  sx={{
                    display: "flex",
                    gap: 3,
                    py: 2,
                    '&:hover': {
                      backgroundColor: 'error.lighter',
                    }
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon color="error" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Delete Portfolio Item"
                    secondary="Permanently remove from portfolio"
                    primaryTypographyProps={{
                      color: 'error.main'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            pb: 2,
          }}>
            <WarningAmberIcon color="error" sx={{ fontSize: 28 }} />
            <Typography variant="h6" component="div">
              Confirm Deletion
            </Typography>
          </DialogTitle>

          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete <strong>{rowData.company}</strong> from your portfolio?
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone. All data associated with this portfolio item will be permanently removed.
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <CommonButton
              variant="outlined"
              onClick={handleCancelDelete}
              disabled={isDeleting}
              sx={{ minWidth: 100 }}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              sx={{ minWidth: 100 }}
              startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </CommonButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default PortfolioActionsModal;